import { Asset } from "../../models/financial/asset.js";
import { validateAndFormatAddress } from "../../utils/validateAndParseAddress.js";
import invariant from "tiny-invariant";
/**
 * Represents an ERC721 token with a unique address and some metadata.
 */
export class NonFungibleToken extends Asset {
    /**
     * @param platformId The platform ID on which this token resides
     * @param address The contract address on the platform on which this token lives
     * @param symbol {@link Asset#symbol}
     * @param name {@link Asset#name}
     * @param url The URL of the token's metadata
     * @param bypassChecksum If true it only checks for length === 42, startsWith 0x and contains only hex characters
     * @param coinGeckoId CoinGecko ID for price lookups
     */
    constructor(platformId, address, symbol, name, url, bypassChecksum, coinGeckoId) {
        super(symbol, name, coinGeckoId);
        this.isNative = false;
        this.isToken = true;
        if (bypassChecksum) {
            const formattedAddress = validateAndFormatAddress(address);
            invariant(formattedAddress, `${address} is not a valid address.`);
            this.address = formattedAddress;
        }
        else {
            const formattedAddress = validateAndFormatAddress(address);
            invariant(formattedAddress, `${address} is not a valid address.`);
            this.address = formattedAddress;
        }
        this.platformId = platformId;
        this.url = url;
    }
    static toNonFungibleToken(nft) {
        if (!nft) {
            return undefined;
        }
        const asset = Asset.toAsset(nft.asset);
        return new NonFungibleToken(nft.platformId ?? nft.chainId, nft.address, asset.symbol, asset.name, nft.url);
    }
    /**
     * Returns true if the two tokens are equivalent, i.e. have the same platformId and address.
     * @param other other token to compare
     */
    equals(other) {
        // short circuit on reference equality
        if (this === other)
            return true;
        return (this.platformId == other.platformId &&
            validateAndFormatAddress(this.address) == validateAndFormatAddress(other.address));
    }
    /**
     * Returns the asset representation of this currency
     */
    asset() {
        return this;
    }
    /**
     * Converts the non-fungible token to a model representation for serialization
     */
    toNonFungibleTokenModel() {
        return {
            __typename: "NonFungibleToken",
            asset: this.toAssetModel(),
            address: validateAndFormatAddress(this.address),
            chainId: this.platformId,
            platformId: this.platformId,
            url: this.url,
        };
    }
    identifier() {
        return `${this.platformId}+${validateAndFormatAddress(this.address)}`;
    }
}
